import { useRef, useCallback } from 'react'
import styles from './WorkingHoursDial.module.css'

interface Props {
  start: string  // "HH:MM"
  end: string    // "HH:MM"
  onChange: (start: string, end: string) => void
}

const HOURS = 24
const TRACK_MAX = 24 * 60  // minutes in a day

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

function toHHMM(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function formatDisplay(hhmm: string): string {
  const mins = toMinutes(hhmm)
  const h = Math.floor(mins / 60) % 24
  const m = mins % 60
  const ampm = h < 12 ? 'am' : 'pm'
  const h12 = h % 12 || 12
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`
}

const SNAP = 30  // snap to 30-minute intervals

export function WorkingHoursDial({ start, end, onChange }: Props) {
  const trackRef = useRef<HTMLDivElement>(null)

  const startMin = toMinutes(start)
  const endMin = toMinutes(end)

  const startPct = (startMin / TRACK_MAX) * 100
  const endPct = (endMin / TRACK_MAX) * 100

  function getMinutesFromEvent(e: MouseEvent | TouchEvent): number {
    if (!trackRef.current) return 0
    const rect = trackRef.current.getBoundingClientRect()
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const raw = pct * TRACK_MAX
    return Math.round(raw / SNAP) * SNAP
  }

  const dragThumb = useCallback((which: 'start' | 'end') => {
    function onMove(e: MouseEvent | TouchEvent) {
      const mins = getMinutesFromEvent(e)
      if (which === 'start') {
        const clamped = Math.min(mins, endMin - SNAP)
        onChange(toHHMM(clamped), end)
      } else {
        const clamped = Math.max(mins, startMin + SNAP)
        onChange(start, toHHMM(clamped))
      }
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('touchend', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('touchmove', onMove)
    window.addEventListener('mouseup', onUp)
    window.addEventListener('touchend', onUp)
  }, [start, end, startMin, endMin, onChange])

  // Hour tick marks
  const ticks = [0, 3, 6, 9, 12, 15, 18, 21, 24]

  return (
    <div className={styles.wrap}>
      {/* Labels */}
      <div className={styles.labels}>
        <span className={styles.labelBadge}>
          {formatDisplay(start)}
        </span>
        <span className={styles.labelArrow}>→</span>
        <span className={styles.labelBadge}>
          {formatDisplay(end)}
        </span>
        <span className={styles.duration}>
          {Math.round((endMin - startMin) / 60)}h window
        </span>
      </div>

      {/* Track */}
      <div className={styles.trackWrap} ref={trackRef}>
        {/* Background track */}
        <div className={styles.track} />

        {/* Active range fill */}
        <div
          className={styles.fill}
          style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
        />

        {/* Start thumb */}
        <div
          className={styles.thumb}
          style={{ left: `${startPct}%` }}
          onMouseDown={() => dragThumb('start')}
          onTouchStart={() => dragThumb('start')}
          role="slider"
          aria-label="Start time"
          aria-valuetext={formatDisplay(start)}
        />

        {/* End thumb */}
        <div
          className={styles.thumb}
          style={{ left: `${endPct}%` }}
          onMouseDown={() => dragThumb('end')}
          onTouchStart={() => dragThumb('end')}
          role="slider"
          aria-label="End time"
          aria-valuetext={formatDisplay(end)}
        />
      </div>

      {/* Tick labels */}
      <div className={styles.ticks}>
        {ticks.map(h => (
          <span
            key={h}
            className={styles.tick}
            style={{ left: `${(h / HOURS) * 100}%` }}
          >
            {h === 0 ? '12am' : h === 12 ? '12pm' : h === 24 ? '' : h < 12 ? `${h}am` : `${h - 12}pm`}
          </span>
        ))}
      </div>
    </div>
  )
}
